// Lib
import { useEffect } from 'react';
import { Editor } from '@tiptap/react';
import getParentScrollableSection from '../../../client/utils/dom/getParentScrollableSection';

/**
 * When the editor starts editing, ensure the caret is placed in the editor.
 */
const useTiptapEditorFocus = (
    editor: Editor | null,
    isEditing: boolean,
    // WEB-13628 - This is a hack to prevent scroll issues when de-indenting tasks - see below for more details.
    _preventScroll = false,
) => {
    useEffect(() => {
        if (!isEditing) return;

        if (!editor) return;

        // WEB-13628 - For tasks we need to defer the focus to avoid scroll issues when de-indenting.
        //  This is because when de-indenting, both the new task & old task are in the DOM, and focusing immediately
        //  causes scroll jumps.
        // Ideally this is a temporary solution and can be removed when task lists become single editors.
        // NOTE: Due to potential hard to find edge cases, I've opted to only enable this for tasks for now, despite
        //  not finding any issues elsewhere, while testing it in other places.
        if (_preventScroll) {
            const ancestorScrollableArea = getParentScrollableSection(editor.view.dom);
            const scrollTopBefore = ancestorScrollableArea?.scrollTop;

            editor?.view.focus();

            requestAnimationFrame(() => {
                const ancestorScrollableArea = getParentScrollableSection(editor.view.dom);

                if (!ancestorScrollableArea) return;

                ancestorScrollableArea.scrollTop = scrollTopBefore || 0;
            });

            return;
        }

        // NOTE: We're intentionally using the `view.focus()` rather than the `commands.focus()` here
        //  as using the command causes issues on iOS (WEB-11786) - the cursor will jump between the start
        //  of the editor & where the user taps.
        //  As far as I've seen there's no regressions elsewhere, but if there are we might want to conditionally
        //  use the command based on the platform.
        editor?.view.focus();
    }, [isEditing]);
};

export default useTiptapEditorFocus;
